<template>
<div class="wrapper">
  <div :class="['address-container', {'up-top': isEnabled && keyword}]">
    <div class="address-container__form">
      <one-line-form :submit="updateAddress" :submittable="isValid" :button="false" :saving="isSaving ? address : ''">
        <input-field name="onelineaddress"
                      id="onelineaddress"
                      type="text"
                      :title="isEnabled ? '' : i18n.t('house:house_number')"
                      :placeholder="i18n.t('house:enter_house_number')"
                      :autocapitalize="true"
                      :autocorrect="false"
                      :value="isEnabled ? keyword : address"
                      ref="input"
                      @keyDown="keydown"
                      @input="getPredictions"
                      autocomplete="cc-csc" />
        <transition name="fade">
          <div class="slot-holder" v-show="isEnabled ? !keyword : true">
            <slot></slot>
          </div>
        </transition>
      </one-line-form>
    </div>
    <transition name="fade">
      <div class="address-container__predictions-container" @touchstart="hideKeyboard" v-if="isEnabled && predictions.length > 0">
        <div class="address-container__predictions-container__list">
          <ul>
            <li v-for="(prediction, index) in predictions" :class="{selected: selectedAnswerIndex === index}" :key="'prediction-'+index" @click="syncAddress(index)">
              <span>{{ prediction.adresse.husnr }}{{ prediction.adresse.etage ? `, ${prediction.adresse.etage}.` : '' }} {{ prediction.adresse.dør ? `${prediction.adresse.dør},` : '' }}</span>
              <small>{{ prediction.adresse.vejnavn }}, {{prediction.adresse.postnr}} {{prediction.adresse.postnrnavn}}</small>
            </li>
          </ul>
        </div>
      </div>
    </transition>
    <div v-if="isEnabled && keyword && predictions.length <= 0" class="address-container__predictions-container__warning">
      <helper :error="true">{{ i18n.t('postalCode:incorrect_address') }}</helper>
    </div>
  </div>
</div>
</template>

<script>
  import Vue from 'vue'
  import { mapState } from 'vuex'
  import { dawaAutocomplete } from '../utils/dawaAutocomplete'

  export default Vue.component('house-number-autofill', {
    props: ['screenParams'],
    data () {
      const address = this.$store.state.user.fullAddressText
      const isEnabled = this.$store.state.user.country === 'DK'
      return {
        selectedAnswerIndex: -1,
        address: address || '',
        keyword: '',
        isSaving: false,
        predictions: [],
        warning: false,
        typingTimeout: null,
        submittable: (!isEnabled && true) || false,
        isEnabled: isEnabled && true
      }
    },
    computed: {
      ...mapState(['app', 'partner']),
      isValid () {
        return this.isEnabled ? this.submittable : typeof this.address === 'string' && this.address.length > 4
      }
    },
    methods: {
      keydown (e) {
        if (e.keyCode === 40) {
          e.preventDefault()
          if (this.selectedAnswerIndex === this.predictions.length - 1) return
          this.selectedAnswerIndex++
        } else if (e.keyCode === 38) {
          e.preventDefault()
          if (this.selectedAnswerIndex === -1) {
            this.selectedAnswerIndex = 0
          } else if (this.selectedAnswerIndex === 0) return
          this.selectedAnswerIndex--
        } else if (e.keyCode === 13 && this.selectedAnswerIndex !== -1) {
          this.syncAddress()
        }
      },
      syncAddress (index) {
        this.selectedAnswerIndex = index !== undefined ? index : this.selectedAnswerIndex
        this.address = this.predictions[this.selectedAnswerIndex].tekst
        this.updateAddress()
      },
      async updateAddress () {
        this.isSaving = true
        this.submittable = true

        const selectedAddress = this.predictions[this.selectedAnswerIndex]
        let address = {}

        address.fullAddressText = selectedAddress.tekst
        address.houseNumber = selectedAddress.adresse.husnr

        if (selectedAddress.adresse.dør) {
          address.door = selectedAddress.adresse.dør
        }

        if (selectedAddress.adresse.etage) {
          address.floor = selectedAddress.adresse.etage
        }

        this.$store.dispatch('updateUser', address)
        setTimeout(() => {
          this.$store.dispatch('next', this.screenParams.nextScreenRoute)
        }, (400))
      },
      getPredictions (value) {
        if (!this.isEnabled) {
          this.address = value
          return
        }
        clearTimeout(this.typingTimeout)
        this.typingTimeout = setTimeout(async () => {
          this.keyword = value
          if (!value) {
            this.predictions = []
            return
          }
          const res = await dawaAutocomplete(value, this.$store)
          this.predictions = res.body || []
          this.selectedAnswerIndex = -1
        }, 200)
      },
      hideKeyboard () {
        document.activeElement.blur()
      }
    }
  })
</script>

<style lang="scss" scoped>
@import '../assets/mixins';
@import '../assets/settings';
@import '../assets/animations';

.wrapper {
  position: absolute;
  top: -30px;
  bottom: -30px;
  left: -30px;
  right: -30px;
  margin-top: -30%;
  display: flex;
  overflow: hidden;
  flex-direction: column;

  @include desktop {
    top: -93px;
    height: 500px;
  }
}

.address-container {
  padding: 30px 0;
  margin-top: 25%;
  position: relative;
  z-index: 20;
  transition: all .4s ease-out;

  @include desktop{
    margin-top: $section-desktop-margin-top;
    position: static;
  }

  &__form {
    transition: all .4s ease-out;
    z-index: 21;
    position: relative;
    padding: 0 30px;

    @include desktop {
      position: static;
    }
  }


  &__predictions-container {
    z-index: 20;
    position: relative;
    width: 100%;
    box-sizing: border-box;
    padding-top: 20px;
    overflow-y: auto;
    height: calc(100% - 72px);
    @include scrollbar;

    &__list {
      min-height: 290px;
      position: relative;
      padding-bottom: 40px;

      img#credit {
        width: 150px;
        object-fit: contain;
        position: absolute;
        left: 0;
        right: 0;
        margin: auto;
        bottom: 0px;
      }
    }

    &__warning {
      padding: 0 30px;
      margin-top: 20px;
      animation: fade-in .4s 1s backwards;
    }

    ul {
      transition: all .4s;
      li {
        cursor: pointer;
        height: 58px;
        width: 100%;
        padding: 10px 0;
        list-style: none;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        justify-content: center;
        position: relative;
        border-bottom: 1px #F1F1F1 solid;

        &:last-child {
          border-color: transparent;
        }

        &.selected {
          background: $inputSaveColor;
          border-color: $inputSaveColor;
          span,
          small {
            color: #FFFFFF;
          }
        }
        span,
        small {
          padding: 0 30px;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
        }
        span {
          color: #004854;
          font-size: 16px;
          font-weight: bold;
          line-height: 20px;
        }
        small {
          color: #B7BFBF;
          font-size: 14px;
          line-height: 17px;
          font-weight: 300;
        }
      }
    }
  }

  &.up-top {
    margin-top: 0;
    padding: 0;
    height: 100%;

    .address-container__form {
      padding: 10px 30px;
      box-shadow: 0 5px 8px 0 rgba(0,0,0,0.08);
      background: #FFFFFF;

      /deep/ .input-wrapper {
        border-color: transparent;
      }

      form {
        margin: 0;
      }
    }
  }


  /deep/ .button-wrapper {
    right: 0;
    top: 0;
    bottom: 0;
    overflow: hidden;
    margin: auto;

    @include desktop {
      top: auto;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/HouseNumberAutofill.vue